import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ImportPanel } from "@/components/ImportPanel";
import { SiteFooter } from "@/components/Chrome";
import { HeroProject } from "@/components/HeroProject";



const STEPS = [
  ["01", "Imagine", "Paste a Zillow link or upload photos. AI shows what the place could become, keeping the same room and layout."],
  ["02", "Propose", "Build a realistic proposal with a budget, sourced products, and required repairs kept separate from optional upgrades."],
  ["03", "Back & Build", "Contributors pledge conditional support. The owner reviews and authorizes. Funds are collected only after approval, then the work gets built. PlaceBacker charges a transparent 5% platform fee only on successfully collected funds."],
  ["04", "Share", "Every project gets a permanent page to rally the people who care about the place."],
];

export default function Home() {
  const nav = useNavigate();
  const urlRef = useRef(null);

  const toImport = () => {
    document.getElementById("import")?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => urlRef.current?.focus({ preventScroll: true }), 450);
  };

  return (
    <main data-testid="home-page">
      <section className="relative overflow-hidden">
        <div className="relative mx-auto grid max-w-7xl gap-0 px-5 pb-12 pt-6 sm:px-8 lg:min-h-[calc(100vh-56px)] lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-8 lg:px-12 lg:pt-14">
          <div className="rise d2 order-6 mt-8 lg:order-2 lg:mt-0"><HeroProject /></div>
          <div className="contents lg:order-1 lg:flex lg:max-w-xl lg:flex-col">
            <p className="eyebrow rise d1 order-1" data-testid="hero-wordmark">PlaceBacker</p>
            <h1 data-testid="hero-headline" className="rise d2 order-2 mt-4 font-display text-[2.2rem] leading-[1.02] tracking-tight text-[var(--charcoal)] sm:text-5xl lg:mt-3 lg:text-6xl">
              Fund what a place could become.
            </h1>
            <p data-testid="hero-subline" className="rise d3 order-3 mt-5 max-w-md text-[0.98rem] leading-relaxed text-[var(--ink-soft)] sm:text-lg">
              Turn overlooked spaces into projects people can back. Imagine the possibilities, build a realistic proposal, bring owners and contributors together, and make the transformation happen.
            </p>
            <div className="rise d4 order-4 mt-7 flex flex-wrap gap-3">
              <button onClick={toImport} className="btn-primary" data-testid="revitalize-place-cta">Start a Project</button>
              <button onClick={() => nav("/explore")} className="btn-ghost" data-testid="explore-possibilities-cta">Explore Projects</button>
            </div>
            <p data-testid="hero-reach-line" className="rise d4 order-5 mt-5 font-mono-ui text-[0.7rem] uppercase leading-relaxed tracking-[0.08em] text-[var(--stone-dark)]">
              From San Francisco apartments to abandoned homes around the world.
            </p>
            <div className="rise d1 order-7 mt-8 lg:mt-12">
              <ImportPanel urlRef={urlRef} />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pt-20 sm:px-8 lg:px-12" data-testid="how-it-works">
        <div className="eyebrow mb-4">The process</div>
        <h2 className="max-w-2xl font-display text-3xl leading-tight tracking-tight sm:text-4xl">From overlooked to underway.</h2>
        <ol className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([n, t, d]) => (
            <li key={n} className="border-t border-[var(--charcoal)]/20 pt-5">
              <div className="font-display text-4xl text-[var(--green)]">{n}</div>
              <div className="mt-3 text-lg font-medium">{t}</div>
              <p className="mt-2 text-sm leading-relaxed text-[var(--ink-soft)]">{d}</p>
            </li>
          ))}
        </ol>
      </section>
      <SiteFooter />
    </main>
  );
}
